import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { compressFilesToWebP } from '@/lib/imageUtils';
import { useForm, Controller } from 'react-hook-form';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AxiosError } from 'axios';
import {
  ArrowDownCircle, Loader2, TrendingDown, TrendingUp, Wallet,
} from 'lucide-react';
import type { ApiError } from '@pms/shared';
import {
  financeService, type ReimbursementCategory, type ReimbursementLedgerEntry,
} from '@/services/finance.service';
import { Tabs } from '@/components/ui/tabs';
import { DataTable, type Column } from '@/components/ui/data-table';
import { StatCard } from '@/components/ui/stat-card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { formatINR } from '@/lib/currency';
import { formatDateTime } from '@/lib/dateFormat';
import { BillLinks, ledgerAmountCell } from './ReimbursementsPage';

function errMsg(e: unknown): string {
  return (e as AxiosError<ApiError>).response?.data?.error?.message ?? 'Something went wrong';
}

const TABS = [
  { key: 'add', label: 'Add Expense' },
  { key: 'ledger', label: 'My Ledger' },
  { key: 'summary', label: 'My Summary' },
];

interface ExpenseForm {
  category_id: string;
  expense_date: string;
  amount: string;
  project_tag: string;
  description: string;
}

function AddExpenseTab({ balance }: { balance: number }) {
  const queryClient = useQueryClient();
  const [files, setFiles] = useState<File[]>([]);
  const { data: categories = [] } = useQuery({
    queryKey: ['finance', 'reimbursement', 'categories'],
    queryFn: () => financeService.listReimbursementCategories(),
  });

  const { register, handleSubmit, reset, setValue, watch, control, formState: { errors } } = useForm<ExpenseForm>({
    defaultValues: { category_id: '', expense_date: '', amount: '', project_tag: '', description: '' },
  });
  const categoryId = watch('category_id');
  const expenseDate = watch('expense_date');
  const selectedCat = categories.find((c) => c.id === categoryId);

  const mutation = useMutation({
    mutationFn: async (values: ExpenseForm) => {
      const fd = new FormData();
      if (values.category_id) fd.append('category_id', values.category_id);
      fd.append('amount', String(Number(values.amount)));
      if (values.expense_date) fd.append('expense_date', values.expense_date);
      if (values.project_tag.trim()) fd.append('project_tag', values.project_tag.trim());
      if (values.description.trim()) fd.append('description', values.description.trim());
      const processedBills = await compressFilesToWebP(files);
      processedBills.forEach((f) => fd.append('bills', f));
      return financeService.addReimbursementExpense(fd);
    },
    onSuccess: () => {
      toast.success('Expense added');
      reset(); setFiles([]);
      void queryClient.invalidateQueries({ queryKey: ['finance', 'reimbursement', 'me'] });
      onSuccessInvalidate(queryClient);
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  return (
    <div className="rounded-xl border bg-card p-3 md:p-6">
      <form onSubmit={handleSubmit((v) => mutation.mutate(v))} className="space-y-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="ex-cat">Expense Category</Label>
            <Controller
              name="category_id" control={control}
              render={({ field }) => (
                <Select value={field.value || undefined} onValueChange={field.onChange}>
                  <SelectTrigger id="ex-cat"><SelectValue placeholder="Select category" /></SelectTrigger>
                  <SelectContent>
                    {categories.map((c: ReimbursementCategory) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
            />
            {selectedCat && Number(selectedCat.per_claim_limit) > 0 && (
              <p className="text-xs text-muted-foreground">Max per claim: {formatINR(selectedCat.per_claim_limit)}</p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ex-date">Expense Date</Label>
            <DatePicker id="ex-date" value={expenseDate} onChange={(v) => setValue('expense_date', v)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ex-amt">Amount (₹) *</Label>
            <Input id="ex-amt" type="number" min="0.01" step="0.01" {...register('amount', {
              required: 'Amount is required',
              validate: (v) => {
                const n = Number(v);
                if (!(n > 0)) return 'Enter a positive amount';
                if (n > balance) return `Insufficient balance (${formatINR(balance)})`;
                return true;
              },
            })} />
            {errors.amount && <p className="text-xs text-destructive">{errors.amount.message}</p>}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ex-tag">Project Tag</Label>
            <Input id="ex-tag" {...register('project_tag')} />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="ex-desc">Description</Label>
          <Textarea id="ex-desc" {...register('description')} rows={2} />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="ex-bills">Upload Bills / Screenshots</Label>
          <Input id="ex-bills" type="file" multiple accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx" onChange={(e) => setFiles(Array.from(e.target.files ?? []))} />
          {selectedCat && Number(selectedCat.bill_required_above) > 0 && (
            <p className="text-xs text-muted-foreground">Bill mandatory above {formatINR(selectedCat.bill_required_above)}</p>
          )}
        </div>
        <Button type="submit" disabled={mutation.isPending}>
          {mutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ArrowDownCircle className="h-4 w-4 mr-2" />}
          Add Expense
        </Button>
      </form>
    </div>
  );
}

function onSuccessInvalidate(queryClient: ReturnType<typeof useQueryClient>) {
  void queryClient.invalidateQueries({ queryKey: ['finance', 'reimbursement', 'me-ledger'] });
}

function MyLedgerTab() {
  const [page, setPage] = useState(1);
  const limit = 15;
  const { data, isLoading, isError } = useQuery({
    queryKey: ['finance', 'reimbursement', 'me-ledger', { page }],
    queryFn: () => financeService.getMyReimbursementLedger({ page, limit }),
  });
  const columns: Column<ReimbursementLedgerEntry>[] = [
    { key: 'date', header: 'Date', cell: (r) => <span className="whitespace-nowrap text-sm">{formatDateTime(r.created_at)}</span> },
    { key: 'type', header: 'Type', cell: (r) => <Badge variant={r.entry_type === 'credit' ? 'success' : 'destructive'}>{r.entry_type === 'credit' ? 'Credit' : 'Debit'}</Badge> },
    { key: 'cat', header: 'Category', cell: (r) => r.category_name ?? <span className="text-muted-foreground">—</span> },
    { key: 'desc', header: 'Description', cell: (r) => <span className="block max-w-[180px] truncate text-sm">{r.description ?? '—'}</span> },
    { key: 'amount', header: 'Amount', className: 'text-right', cell: ledgerAmountCell },
    { key: 'balance', header: 'Balance', className: 'text-right', cell: (r) => <span className="font-mono tabular-nums">{formatINR(r.running_balance)}</span> },
    { key: 'bills', header: 'Bills', cell: (r) => <BillLinks entry={r} /> },
  ];
  const pagination = data ? { page: data.pagination.page, totalPages: data.pagination.totalPages, total: data.pagination.total, onPageChange: setPage } : undefined;
  return <DataTable columns={columns} rows={data?.data ?? []} rowKey={(r) => r.id} loading={isLoading} error={isError} empty="No transactions yet." pagination={pagination} />;
}

export function MyReimbursementPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') ?? 'add';
  const setTab = (key: string) => setSearchParams({ tab: key });

  const { data, isLoading } = useQuery({
    queryKey: ['finance', 'reimbursement', 'me'],
    queryFn: () => financeService.getMyReimbursement(),
  });

  const account = data?.account ?? null;
  const balance = account ? Number(account.balance) : 0;

  const summaryCards = useMemo(() => ([
    { label: 'Current Balance', value: formatINR(balance), tone: 'teal' as const, icon: Wallet },
    { label: 'Total Credited', value: formatINR(account?.total_credited ?? 0), tone: 'emerald' as const, icon: TrendingUp },
    { label: 'Month Credit', value: formatINR(data?.month_credit ?? 0), tone: 'sky' as const, icon: ArrowDownCircle },
    { label: 'Month Debit', value: formatINR(data?.month_debit ?? 0), tone: 'rose' as const, icon: TrendingDown },
  ]), [balance, account, data]);

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl sm:text-2xl font-semibold tracking-tight">Reimbursement</h1>
        <p className="text-sm text-muted-foreground">Manage your expense account</p>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-border/60 bg-gradient-to-br from-teal-50 to-card dark:from-teal-900/20 dark:to-card p-5">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary"><Wallet className="h-6 w-6" /></div>
          <div>
            <p className="text-xs uppercase tracking-wider text-muted-foreground">Available Balance</p>
            <p className="text-2xl font-bold">{formatINR(balance)}</p>
          </div>
        </div>
        <div className="flex gap-8 text-right">
          <div><p className="text-xs text-muted-foreground">Total Credited</p><p className="font-semibold text-emerald-600 dark:text-emerald-400">{formatINR(account?.total_credited ?? 0)}</p></div>
          <div><p className="text-xs text-muted-foreground">Total Debited</p><p className="font-semibold text-rose-600 dark:text-rose-400">{formatINR(account?.total_debited ?? 0)}</p></div>
        </div>
      </div>

      {isLoading ? (
        <div className="h-32 animate-pulse rounded-xl bg-muted/40" />
      ) : !account ? (
        <div className="rounded-xl border bg-card p-8 text-center text-sm text-muted-foreground">
          Your reimbursement account has not been created yet. Please contact your admin/finance team.
        </div>
      ) : (
        <>
          <Tabs tabs={TABS} active={tab} onChange={setTab} />
          <div className="mt-2">
            {tab === 'add' && <AddExpenseTab balance={balance} />}
            {tab === 'ledger' && <MyLedgerTab />}
            {tab === 'summary' && (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {summaryCards.map((c) => <StatCard key={c.label} label={c.label} value={c.value} tone={c.tone} icon={c.icon} />)}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
